const FALLBACK_API_SEARCH_URL = 'https://services.mybcapps.com/bc-sf-filter/search';
const ITEMS_PER_PAGE = 12;
const SEARCH_REDIRECTS_PAGE_LIMIT = 20;

const SEARCH_TYPE_CONFIG = {
  product: { pathSuffix: '', hasFilters: true },
  collections: { pathSuffix: '/collections', hasFilters: false },
  pages: { pathSuffix: '/pages', hasFilters: false },
  'pages-services': { pathSuffix: '/pages', hasFilters: false },
};

function getBoostConfig() {
  return window.boostPFSConfig || {};
}

function getBoostSearchBaseUrl() {
  return getBoostConfig().api?.searchUrl || FALLBACK_API_SEARCH_URL;
}

function getBoostRedirectsUrl() {
  return `${getBoostSearchBaseUrl()}/redirects`;
}

function normalizeRedirectTerm(term) {
  return String(term || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function localizeRedirectUrl(redirectTo, rootUrl) {
  if (!redirectTo || typeof redirectTo !== 'string') return redirectTo;

  let parsed;
  try {
    parsed = new URL(redirectTo, window.location.origin);
  } catch {
    return redirectTo;
  }

  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(redirectTo) || redirectTo.startsWith('//');
  if (isAbsolute) {
    const shopDomain = getBoostConfig().shop?.domain;
    let shopHost = '';
    try {
      const shopUrl = getBoostConfig().shop?.url;
      if (shopUrl) shopHost = new URL(shopUrl).hostname;
    } catch {
      // ignore
    }
    if (
      parsed.hostname !== window.location.hostname &&
      parsed.hostname !== shopDomain &&
      parsed.hostname !== shopHost
    ) {
      return redirectTo;
    }
  }

  const segments = parsed.pathname.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  const locales = getBoostConfig().general?.published_locales || {};
  if (
    first &&
    (Object.keys(locales).some((code) => code.toLowerCase() === first) ||
      /^[a-z]{2}-[a-z]{2}$/.test(first))
  ) {
    segments.shift();
  }

  const root = rootUrl || window.theme?.routes?.root_url || '/';
  const prefix = !root || root === '/' ? '' : `/${String(root).replace(/^\/+|\/+$/g, '')}`;
  const path = segments.length ? `/${segments.join('/')}` : '';

  return `${prefix}${path || (prefix ? '' : '/')}${parsed.search}${parsed.hash}`;
}

class BoostSearch extends HTMLElement {
  constructor() {
    super();
    this.selectedFilters = new Map();
    this.currentQuery = '';
    this.currentPage = 0;
    this.currentSearchType = 'product';
    this.currentSort = 'relevance';
    this.abortController = null;
    this.requestId = 0;
    this.hasFilterTree = false;
    this.lastFilterOptions = null;
    this.filteredPagesCache = null;
    this.lastPageSearchMeta = null;
    this.filteredCollectionsCache = null;
    this.lastCollectionSearchMeta = null;
    this.pagesVisibleCount = ITEMS_PER_PAGE;
    this.infiniteObserver = null;
    this.isLoadingMorePages = false;
    this.infoTemplate = null;
    this.pageCountsCache = null;
    this.lastSearchTotals = null;
    this.redirectsMap = null;
    this.redirectsPromise = null;
    this.onPopState = this.onPopState.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onTabClick = this.onTabClick.bind(this);
    this.onPaginationClick = this.onPaginationClick.bind(this);
    this.onProductClick = this.onProductClick.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
    this.onFilterChange = this.onFilterChange.bind(this);
    this.onDrawerControlClick = this.onDrawerControlClick.bind(this);
    this.onCancelClick = this.onCancelClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.placeholder = this.querySelector('.boost-search__placeholder');
  }

  connectedCallback() {
    const boostConfig = getBoostConfig();

    this.shop = boostConfig.shop?.domain || this.dataset.shop || '';
    this.limit = ITEMS_PER_PAGE;
    this.rootUrl = this.dataset.rootUrl || '/';
    this.currency =
      boostConfig.general?.current_currency ||
      this.dataset.currency ||
      window.Shopify?.currency?.active ||
      '';
    this.country = this.dataset.country || window.Shopify?.country || '';

    this.querySelector('.boost-search__form')?.addEventListener('submit', this.onSubmit);
    this.cancelButton?.addEventListener('click', this.onCancelClick);
    this.input?.addEventListener('input', this.onInput);
    this.resultsContainer?.addEventListener('click', this.onPaginationClick);
    this.resultsContainer?.addEventListener('click', this.onProductClick);
    this.sortingContainer?.addEventListener('change', this.onSortChange);
    this.filtersDrawerContent?.addEventListener('change', this.onFilterChange);
    this.addEventListener('click', this.onDrawerControlClick);
    window.addEventListener('popstate', this.onPopState);

    this.initTabs();
    this.cacheInfoTemplate();
    this.syncFromUrl();
    this.updateCancelButtonVisibility();
    this.prefetchRedirects();
    this.init();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    this.cancelButton?.removeEventListener('click', this.onCancelClick);
    this.input?.removeEventListener('input', this.onInput);
    this.resultsContainer?.removeEventListener('click', this.onPaginationClick);
    this.resultsContainer?.removeEventListener('click', this.onProductClick);
    this.sortingContainer?.removeEventListener('change', this.onSortChange);
    this.filtersDrawerContent?.removeEventListener('change', this.onFilterChange);
    this.removeEventListener('click', this.onDrawerControlClick);
    this.disconnectInfiniteScroll();
  }

  get input() {
    return this.querySelector('.boost-search__form input[type="search"], .boost-search__form input[type="text"]');
  }

  get cancelButton() {
    return this.querySelector('.boost-search__cancel');
  }

  get filtersTrigger() {
    return this.querySelector('.boost-search__filters-trigger');
  }

  get filtersDrawer() {
    return this.querySelector('#BoostFacetDrawer');
  }

  get filtersDrawerContent() {
    return this.querySelector('#BoostFacetFiltersContainer');
  }

  get toolbar() {
    return this.querySelector('.boost-search__toolbar');
  }

  get countElement() {
    return this.querySelector('.boost-search__count');
  }

  get infoElement() {
    return this.querySelector('[data-boost-search-info]');
  }

  cacheInfoTemplate() {
    const element = this.infoElement;
    if (!element || this.infoTemplate) {
      return;
    }

    this.infoTemplate = element.innerHTML;
  }

  updateSearchInfo(count = this.lastTotalItems) {
    const element = this.infoElement;
    const query = this.currentQuery || this.getQuery();

    if (!element || !this.infoTemplate) {
      return;
    }

    if (!query) {
      element.hidden = true;
      return;
    }

    element.innerHTML = this.infoTemplate
      .replace(/\{count\}/g, String(count ?? 0))
      .replace(/\{search\}/g, this.escapeHtml(query));
    element.hidden = false;
  }

  hideSearchInfo() {
    if (this.infoElement) {
      this.infoElement.hidden = true;
    }
  }

  get sortingContainer() {
    return this.querySelector('.boost-search__sorting');
  }

  get resultsContainer() {
    return this.querySelector('.boost-search__results');
  }

  get tabs() {
    return this.querySelectorAll('.search-tab [data-search-type]');
  }

  getSearchTypeConfig(type = this.currentSearchType) {
    return SEARCH_TYPE_CONFIG[type] || SEARCH_TYPE_CONFIG.product;
  }

  getActiveTab(type = this.currentSearchType) {
    return [...this.tabs].find((tab) => tab.dataset.searchType === type) || null;
  }

  getCurrentResultsType(type = this.currentSearchType) {
    return this.getActiveTab(type)?.dataset.resultsType || 'card';
  }

  renderEmptyState(text, type = this.currentSearchType) {
    return `
      <div class="boost-search__empty-state">
        <p class="boost-search__empty">${this.escapeHtml(text)}</p>
        ${this.renderEmptyButton(type)}
      </div>
    `;
  }

  renderEmptyButton(type = this.currentSearchType) {
    const tab = this.getActiveTab(type);
    const label = tab?.dataset.emptyButtonLabel?.trim();
    const url = tab?.dataset.emptyButtonUrl?.trim();

    if (!label || !url) return '';

    return `
      <a class="button button--primary boost-search__empty-button" href="${this.escapeHtml(url)}" is="hover-link">
        <span class="btn-fill" data-fill></span>
        <span class="btn-text">${this.escapeHtml(label)}</span>
      </a>
    `;
  }

  isProductSearch() {
    return this.currentSearchType === 'product';
  }

  isPageSearch() {
    return this.currentSearchType === 'pages' || this.currentSearchType === 'pages-services';
  }

  isCollectionSearch() {
    return this.currentSearchType === 'collections';
  }

  invalidatePageCache({ keepPageCounts = false } = {}) {
    this.filteredPagesCache = null;
    this.lastPageSearchMeta = null;
    this.filteredCollectionsCache = null;
    this.lastCollectionSearchMeta = null;
    this.pagesVisibleCount = this.limit;
    if (!keepPageCounts) {
      this.pageCountsCache = null;
    }
  }

  mergeSearchTotals(data) {
    if (!data) return;

    const prev = this.lastSearchTotals || {};
    this.lastSearchTotals = {
      total_product: Math.max(Number(data.total_product) || 0, prev.total_product || 0),
      total_collection: Math.max(Number(data.total_collection) || 0, prev.total_collection || 0),
      total_page: Math.max(Number(data.total_page) || 0, prev.total_page || 0),
    };
  }

  getTotalsFrom(data) {
    return {
      total_product: data?.total_product ?? this.lastSearchTotals?.total_product ?? 0,
      total_collection: data?.total_collection ?? this.lastSearchTotals?.total_collection ?? 0,
      total_page: data?.total_page ?? this.lastSearchTotals?.total_page ?? 0,
      products: data?.products,
      collections: data?.collections,
      pages: data?.pages,
    };
  }

  getSearchApiUrl(type = this.currentSearchType) {
    const { pathSuffix } = this.getSearchTypeConfig(type);
    return `${getBoostSearchBaseUrl()}${pathSuffix}`;
  }

  getDefaultSearchType() {
    return this.getAvailableSearchTypes()[0] || this.tabs[0]?.dataset.searchType || 'product';
  }

  isTabVisible(tab) {
    if (!tab || tab.disabled) return false;
    if (tab.hidden || tab.getAttribute('aria-hidden') === 'true') return false;

    const item = tab.closest('.search-tab__item');
    if (item?.hidden || item?.classList.contains('hidden')) return false;

    return true;
  }

  getAvailableSearchTypes() {
    return [...this.tabs]
      .filter((tab) => this.isTabVisible(tab))
      .map((tab) => tab.dataset.searchType)
      .filter((type) => type && SEARCH_TYPE_CONFIG[type]);
  }

  applySearchType(searchType, { keepPageCounts = false } = {}) {
    this.currentSearchType = searchType;
    this.currentPage = 0;
    this.invalidatePageCache({ keepPageCounts });

    if (!this.isProductSearch()) {
      this.selectedFilters.clear();
      this.hasFilterTree = false;
    }
  }

  getCurrentSearchResultCount(data) {
    if (this.isPageSearch()) {
      return (this.filteredPagesCache || []).length;
    }

    if (this.isCollectionSearch()) {
      return (this.filteredCollectionsCache || []).length;
    }

    return data?.total_product ?? data?.products?.length ?? 0;
  }

  async ensurePageTypeCounts(query = this.currentQuery) {
    if (this.pageCountsCache?.query === query) {
      return this.pageCountsCache;
    }

    const data = await this.fetchAllBoostPages({
      eventType: 'filter',
      query,
      searchType: 'pages',
      renderErrorOnFail: false,
    });

    if (!data) {
      return { query, page: 0, service: 0, data: null };
    }

    const page = this.filterPagesByBoostConfig(data.pages || [], 'page').length;
    const service = this.filterPagesByBoostConfig(data.pages || [], 'service').length;
    this.pageCountsCache = { query, page, service, data };
    return this.pageCountsCache;
  }

  async tabLikelyHasResults(type, data) {
    const totals = this.getTotalsFrom(data);

    if (type === 'product') {
      return (totals.total_product ?? totals.products?.length ?? 0) > 0;
    }

    if (type === 'collections') {
      if (totals.collections) {
        return this.filterVisibleCollections(totals.collections).length > 0;
      }
      return (totals.total_collection ?? 0) > 0;
    }

    if (type === 'pages' || type === 'pages-services') {
      if (totals.total_page === 0) {
        return false;
      }

      const counts = await this.ensurePageTypeCounts();
      return type === 'pages-services' ? counts.service > 0 : counts.page > 0;
    }

    return false;
  }

  async findNextTabWithResults(tried, data) {
    for (const type of this.getAvailableSearchTypes()) {
      if (tried.has(type)) continue;
      if (await this.tabLikelyHasResults(type, data)) {
        return type;
      }
    }

    return null;
  }

  renderDeferredResults(data) {
    if (this.isPageSearch()) {
      this.hideToolbar();
      this.renderPageResultsFromCache();
      return;
    }

    if (this.isCollectionSearch()) {
      this.hideToolbar();
      this.renderCollectionResultsFromCache();
      return;
    }

    this.renderResults(data);
  }

  getSearchTypeFromUrl() {
    const type = new URLSearchParams(window.location.search).get('type');
    return type && SEARCH_TYPE_CONFIG[type] ? type : null;
  }

  initTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
    });
  }

  updateTabState(button, isActive) {
    button.classList.toggle('button--primary', isActive);
    button.classList.toggle('button--third', !isActive);
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');
  }

  syncTabState() {
    this.tabs.forEach((tab) => {
      this.updateTabState(tab, tab.dataset.searchType === this.currentSearchType);
    });
    this.updateFiltersVisibility();
    this.updateToolbarVisibility();
  }

  updateFiltersVisibility() {
    if (!this.isProductSearch()) {
      if (this.filtersDrawerContent) {
        this.filtersDrawerContent.innerHTML = '';
      }

      if (this.filtersTrigger) {
        this.filtersTrigger.innerHTML = '';
        this.filtersTrigger.hidden = true;
      }

      this.filtersDrawer?.hide?.();
    }
  }

  updateToolbarVisibility() {
    if (!this.toolbar) return;

    const shouldShow = this.isProductSearch() && Boolean(this.currentQuery);
    this.toolbar.classList.toggle('hidden', !shouldShow);
    this.toolbar.hidden = !shouldShow;

    if (!shouldShow) {
      return;
    }

    this.renderFilterTrigger();

    const showSort = (this.lastTotalItems || 0) > 0;
    if (this.sortingContainer) {
      this.sortingContainer.hidden = !showSort;
      this.sortingContainer.classList.toggle('hidden', !showSort);
    }
  }

  getFilterCount() {
    let count = 0;

    for (const values of this.selectedFilters.values()) {
      count += values.size;
    }

    return count;
  }

  updateFilterCounts() {
    const count = this.getFilterCount();
    const hidden = count === 0;

    this.querySelectorAll('facet-count').forEach((element) => {
      element.textContent = String(count);
      element.hidden = hidden;
      element.classList.toggle('hidden', hidden);
    });
  }

  renderFilterIcon() {
    return `
      <svg class="icon icon-filter" viewBox="0 0 22 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path stroke-linecap="round" d="M1.5 14H4M4 14C4 15.6569 5.34315 17 7 17C8.65685 17 10 15.6569 10 14C10 12.3431 8.65685 11 7 11C5.34315 11 4 12.3431 4 14ZM18 6H20.5M18 6C18 7.65685 16.6569 9 15 9C13.3431 9 12 7.65685 12 6C12 4.34315 13.3431 3 15 3C16.6569 3 18 4.34315 18 6ZM13 14H20.5M1.5 6H9"></path>
      </svg>
    `;
  }

  renderChevronIcon() {
    return `
      <svg class="icon icon-chevron-up" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 15L12 9L18 15"></path>
      </svg>
    `;
  }

  renderFilterTrigger() {
    const container = this.filtersTrigger;
    if (!container) return;

    const count = this.getFilterCount();
    const hasOptions = (this.lastFilterOptions?.length || 0) > 0;
    const show = this.isProductSearch() && hasOptions && Boolean(this.currentQuery);

    if (!show) {
      container.innerHTML = '';
      container.hidden = true;
      return;
    }

    const filterShow = this.dataset.filterShow || 'Show filters';
    const countHidden = count === 0 ? 'hidden' : '';

    container.hidden = false;
    container.innerHTML = `
      <div class="facet-wrapper">
        <button class="boost-search__filters-btn" type="button" is="hover-button" aria-controls="BoostFacetDrawer" aria-expanded="false">
          <span class="btn-fill" data-fill></span>
          <span class="btn-text">
            ${this.renderFilterIcon()}
            <span class="btn-text-inner">
              ${this.escapeHtml(filterShow)}
            </span>
            <facet-count class="count flex items-center justify-center text-xs font-medium rounded-full" ${countHidden}>${count}</facet-count>
          </span>
        </button>
      </div>
    `;
  }

  onDrawerControlClick(event) {
    const control = event.target.closest('[aria-controls="BoostFacetDrawer"]');
    const drawer = this.filtersDrawer;

    if (!control || !drawer || !this.filtersTrigger?.contains(control)) {
      return;
    }

    event.preventDefault();

    if (drawer.open) {
      drawer.hide();
    } else {
      drawer.show(control);
    }
  }

  getDefaultSort() {
    const boostConfig = getBoostConfig();
    const searchDefault = boostConfig.default_sort_order?.search;

    if (searchDefault) {
      return searchDefault;
    }

    return 'relevance';
  }

  getSortOptions() {
    const boostConfig = getBoostConfig();
    const sortByOptions = boostConfig.settings?.label?.sortByOptions;
    
    if (sortByOptions.hasOwnProperty('sorting')) {
      delete sortByOptions['sorting'];
    }

    if (sortByOptions && typeof sortByOptions === 'object') {
      const sortingList = boostConfig.settings?.general?.sortingList;
      const keys = Array.isArray(sortingList) && sortingList.length
        ? sortingList.filter((key) => sortByOptions[key])
        : Object.keys(sortByOptions);

      return keys.map((value) => ({
        value,
        name: sortByOptions[value],
      }));
    }

    return [{ value: 'relevance', name: 'Relevance' }];
  }

  getSortLabel(value = this.currentSort) {
    return this.getSortOptions().find((option) => option.value === value)?.name || value;
  }

  getSortFromUrl() {
    const sortBy = new URLSearchParams(window.location.search).get('sort_by');
    const options = this.getSortOptions();

    if (sortBy && options.some((option) => option.value === sortBy)) {
      return sortBy;
    }

    return this.getDefaultSort();
  }

  onTabClick(event) {
    const button = event.currentTarget;
    const searchType = button.dataset.searchType;

    if (!searchType || searchType === this.currentSearchType) {
      return;
    }

    this.currentSearchType = searchType;
    this.currentPage = 0;
    this.invalidatePageCache();

    if (!this.isProductSearch()) {
      this.selectedFilters.clear();
      this.hasFilterTree = false;
    }

    this.syncTabState();
    this.updateUrl({ push: true });

    if (this.currentQuery) {
      this.search({ rebuildTree: this.isProductSearch() });
      return;
    }

    this.clearResults({ keepFilters: false });

    if (this.isProductSearch()) {
      this.loadFilters();
    }
  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  getQueryFromUrl() {
    return new URLSearchParams(window.location.search).get('q')?.trim() || '';
  }

  getPageFromUrl() {
    const page = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
    return Number.isFinite(page) && page > 0 ? page - 1 : 0;
  }

  normalizeFilterParamKey(key) {
    return key.endsWith('[]') ? key.slice(0, -2) : key;
  }

  isFilterParam(key) {
    const optionId = this.normalizeFilterParamKey(key);
    return optionId.startsWith('pf_') && !optionId.endsWith('_and_condition');
  }

  getFilterParamKey(optionId) {
    return `${optionId}[]`;
  }

  syncFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    this.selectedFilters.clear();

    for (const [key, value] of params.entries()) {
      if (!this.isFilterParam(key)) continue;

      const optionId = this.normalizeFilterParamKey(key);

      if (!this.selectedFilters.has(optionId)) {
        this.selectedFilters.set(optionId, new Set());
      }

      this.selectedFilters.get(optionId).add(value);
    }
  }

  syncFromUrl() {
    const query = this.getQueryFromUrl();
    if (this.input) {
      this.input.value = query;
    }
    this.currentQuery = query;
    this.currentSearchType = this.getSearchTypeFromUrl() || this.getDefaultSearchType();
    this.currentPage = this.isPageSearch() ? 0 : this.getPageFromUrl();

    if (this.isProductSearch()) {
      this.syncFiltersFromUrl();
      this.currentSort = this.getSortFromUrl();
    } else {
      this.selectedFilters.clear();
    }

    this.syncTabState();
    this.updateCancelButtonVisibility();
  }

  updateCancelButtonVisibility() {
    const hasValue = Boolean(this.getQuery());
    this.cancelButton?.toggleAttribute('hidden', !hasValue);
  }

  onInput() {
    this.updateCancelButtonVisibility();
  }

  onCancelClick(event) {
    event.preventDefault();

    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }

    this.currentQuery = '';
    this.currentPage = 0;
    this.invalidatePageCache();

    if (this.isProductSearch()) {
      this.selectedFilters.clear();
      this.currentSort = this.getDefaultSort();
    }

    this.updateUrl({ query: '', push: true });
    this.clearResults({ keepFilters: false });
    this.updateCancelButtonVisibility();

    if (this.isProductSearch()) {
      this.loadFilters();
    }
  }

  onProductClick(event) {
    const productLink = event.target.closest('[data-boost-product-id]');
    if (!productLink || !this.resultsContainer?.contains(productLink)) return;

    window.boostAnalytics?.trackProduct({
      productId: productLink.dataset.boostProductId,
      requestId: productLink.dataset.boostRequestId,
      queryString: this.getAnalyticsQueryString(),
      action: 'search',
    });
  }

  getAnalyticsQueryString() {
    const params = new URLSearchParams();
    if (this.currentQuery) params.set('q', this.currentQuery);

    for (const [optionId, values] of this.selectedFilters) {
      for (const value of values) {
        params.append(this.getFilterParamKey(optionId), value);
      }
    }

    return params.toString();
  }

  appendFiltersToUrl(url) {
    for (const [optionId, values] of this.selectedFilters) {
      for (const value of values) {
        url.searchParams.append(this.getFilterParamKey(optionId), value);
      }
    }
  }

  updateUrl({ query, push = true } = {}) {
    const url = new URL(window.location.href);

    for (const key of [...url.searchParams.keys()]) {
      if (this.isFilterParam(key)) {
        url.searchParams.delete(key);
      }
    }

    const searchQuery = query !== undefined ? query : this.currentQuery;

    if (searchQuery) {
      url.searchParams.set('q', searchQuery);
    } else {
      url.searchParams.delete('q');
    }

    const defaultType = this.getDefaultSearchType();
    if (this.currentSearchType && this.currentSearchType !== defaultType) {
      url.searchParams.set('type', this.currentSearchType);
    } else {
      url.searchParams.delete('type');
    }

    if (this.isProductSearch()) {
      this.appendFiltersToUrl(url);
      const defaultSort = this.getDefaultSort();
      if (this.currentSort && this.currentSort !== defaultSort) {
        url.searchParams.set('sort_by', this.currentSort);
      } else {
        url.searchParams.delete('sort_by');
      }
    } else {
      url.searchParams.delete('sort_by');
    }

    if (!this.isPageSearch() && this.currentPage > 0) {
      url.searchParams.set('page', String(this.currentPage + 1));
    } else {
      url.searchParams.delete('page');
    }

    const nextUrl = url.toString();

    if (nextUrl === window.location.href) {
      return;
    }

    const state = { boostSearch: true };

    if (push) {
      window.history.pushState(state, '', nextUrl);
    } else {
      window.history.replaceState(state, '', nextUrl);
    }
  }

  prefetchRedirects() {
    this.loadRedirects();
  }

  buildRedirectsParams(page) {
    const params = new URLSearchParams({
      shop: this.shop,
      limit: String(SEARCH_REDIRECTS_PAGE_LIMIT),
      page: String(page),
    });
    const locale = getBoostConfig().general?.current_locale;
    if (locale) {
      params.set('locale', locale);
    }
    return params;
  }

  async fetchRedirectsPage(page) {
    const response = await fetch(`${getBoostRedirectsUrl()}?${this.buildRedirectsParams(page)}`);
    if (!response.ok) {
      throw new Error(`Boost redirects API responded with ${response.status}`);
    }
    return response.json();
  }

  addRedirectsToMap(map, items) {
    for (const item of items || []) {
      const redirectTo = item.redirect_to;
      if (!redirectTo) continue;

      for (const term of item.terms || []) {
        const key = normalizeRedirectTerm(term);
        if (key) {
          map.set(key, redirectTo);
        }
      }
    }
  }

  async loadRedirects() {
    if (this.redirectsMap) return this.redirectsMap;
    if (this.redirectsPromise) return this.redirectsPromise;

    this.redirectsPromise = (async () => {
      try {
        if (!this.shop) return new Map();

        const firstData = await this.fetchRedirectsPage(1);
        const map = new Map();
        this.addRedirectsToMap(map, firstData.data);

        const total = firstData.total ?? map.size;
        const totalPages = Math.ceil(total / SEARCH_REDIRECTS_PAGE_LIMIT);

        if (totalPages > 1) {
          const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) => this.fetchRedirectsPage(index + 2))
          );

          for (const pageData of rest) {
            this.addRedirectsToMap(map, pageData.data);
          }
        }

        this.redirectsMap = map;
        return map;
      } catch (error) {
        console.error('[boost-search] redirects', error);
        this.redirectsPromise = null;
        return new Map();
      }
    })();

    return this.redirectsPromise;
  }

  async getRedirectUrlForQuery(query) {
    const key = normalizeRedirectTerm(query);
    if (!key) return null;

    const redirects = await this.loadRedirects();
    const redirectTo = redirects.get(key);
    if (!redirectTo) return null;

    return localizeRedirectUrl(redirectTo, this.rootUrl);
  }

  async maybeRedirectForQuery(query) {
    const redirectUrl = await this.getRedirectUrlForQuery(query);
    if (!redirectUrl) return false;

    window.location.href = redirectUrl;
    return true;
  }

  async init() {
    const query = this.getQueryFromUrl();

    if (query) {
      if (await this.maybeRedirectForQuery(query)) {
        return;
      }

      this.currentQuery = query;
      await this.search({ rebuildTree: true, autoSelectTab: true });
      return;
    }

    this.hideSearchInfo();

    if (this.isProductSearch()) {
      await this.loadFilters();
    }
  }

  isSearchPage() {
    const searchPath = new URL(this.dataset.searchUrl || '/search', window.location.origin).pathname;
    const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
    const normalizedSearchPath = searchPath.replace(/\/$/, '') || '/';

    return currentPath === normalizedSearchPath;
  }

  async onSubmit(event) {
    event.preventDefault();

    const query = this.getQuery();

    if (query && (await this.maybeRedirectForQuery(query))) {
      return;
    }

    if (!this.isSearchPage()) {
      const searchUrl = new URL(this.dataset.searchUrl || '/search', window.location.origin);
      if (query) {
        searchUrl.searchParams.set('q', query);
      }
      if (!this.isProductSearch()) {
        searchUrl.searchParams.set('type', this.currentSearchType);
      } else {
        this.appendFiltersToUrl(searchUrl);
      }
      window.location.href = searchUrl.toString();
      return;
    }

    this.currentPage = 0;
    this.invalidatePageCache();
    if (this.isProductSearch()) {
      this.selectedFilters.clear();
      this.currentSort = this.getDefaultSort();
    }
    this.currentQuery = query;
    this.updateUrl({ query, push: true });

    if (!query) {
      this.clearResults({ keepFilters: false });
      if (this.isProductSearch()) {
        this.loadFilters();
      }
      this.updateCancelButtonVisibility();
      return;
    }

    this.updateCancelButtonVisibility();
    this.search({ rebuildTree: true, autoSelectTab: true });
  }

  onPopState() {
    this.hasFilterTree = false;
    this.invalidatePageCache();
    this.syncFromUrl();
    this.init();
  }

  getFilterOptions(filter) {
    if (!filter) return [];
    if (Array.isArray(filter)) return filter;
    return filter.options || [];
  }

  getFilterValues(option) {
    return Array.isArray(option?.values) ? option.values : [];
  }

  isRangeFilter(option) {
    return option?.displayType === 'range' || option?.filterType === 'price';
  }

  getRangeBounds(option) {
    const values = option?.values;
    if (!values || Array.isArray(values)) return null;

    const min = Number(values.min);
    const max = Number(values.max);

    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;

    return {
      min: Math.max(0, Math.floor(min)),
      max: Math.max(0, Math.ceil(max)),
    };
  }

  getSelectedRange(optionId, bounds) {
    const selected = this.selectedFilters.get(optionId);
    const raw = selected ? [...selected][0] : null;

    if (!raw || !String(raw).includes(':')) {
      return { min: 0, max: bounds.max, isActive: false };
    }

    const [from, to] = String(raw).split(':');
    const min = from !== '' && Number.isFinite(Number(from)) ? Number(from) : 0;
    const max = to !== '' && Number.isFinite(Number(to)) ? Number(to) : bounds.max;

    return {
      min: Math.min(Math.max(0, Math.ceil(min)), bounds.max),
      max: Math.max(Math.min(bounds.max, Math.ceil(max)), 0),
      isActive: true,
    };
  }

  normalizeFilterOptions(options = []) {
    return options.filter((option) => {
      if (option.status === 'inactive') return false;
      if (this.isRangeFilter(option)) return Boolean(this.getRangeBounds(option));
      return this.getFilterValues(option).length > 0;
    });
  }

  shouldRenderFilterTree(eventType) {
    return eventType === 'init' || eventType === 'filter';
  }

  getCurrencyRate() {
    const rate = this.dataset.currencyRate ?? window.Shopify?.currency?.rate;
    return rate != null && rate !== '' ? String(rate) : '1';
  }

  appendLocalizationParams(params) {
    const currency = this.currency || this.dataset.currency || window.Shopify?.currency?.active;
    const country = this.country || this.dataset.country || window.Shopify?.country;

    if (currency) {
      params.set('currency', currency);
    }

    params.set('currency_rate', this.getCurrencyRate());

    if (country) {
      params.set('country', country);
    }

    const locale = getBoostConfig().general?.current_locale;
    if (locale) {
      params.set('locale', locale);
    }
  }

  getBoostApiPage(pageIndex = this.currentPage) {
    return pageIndex + 1;
  }

  buildParams({ eventType = 'filter', rebuildTree = false, query, searchType = this.currentSearchType } = {}) {
    const searchQuery = query ?? this.currentQuery;
    const params = new URLSearchParams();
    params.set('shop', this.shop);
    params.set('q', searchQuery);
    params.set('limit', String(this.limit));
    params.set('page', String(this.getBoostApiPage()));
    window.boostAnalytics?.appendEventType(params, eventType);
    this.appendLocalizationParams(params);

    if (searchType !== 'product') {
      return params;
    }

    params.set('search_filter_tree', 'true');
    params.set('zero_options', 'true');

    const needsFilterTree =
      eventType !== 'page' &&
      eventType !== 'sort' &&
      (rebuildTree !== false || eventType === 'init');
    params.set('build_filter_tree', needsFilterTree ? 'true' : 'false');

    if (this.currentSort) {
      params.set('sort', this.currentSort);
    }

    for (const [optionId, values] of this.selectedFilters) {
      for (const value of values) {
        params.append(this.getFilterParamKey(optionId), value);
      }
    }

    return params;
  }

  async fetchBoost({ eventType = 'filter', rebuildTree = false, renderResults = true, renderFilters = true, query } = {}) {
    if (!this.shop) {
      console.error('[boost-search] Missing Boost shop domain in boostPFSConfig');
      return null;
    }

    const searchQuery = query ?? this.currentQuery;
    const shouldRenderFilters = renderFilters && this.isProductSearch();

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const requestId = ++this.requestId;

    this.setLoading(true);

    try {
      const params = this.buildParams({
        eventType,
        rebuildTree: rebuildTree ?? true,
        query: searchQuery,
      });

      const response = await fetch(`${this.getSearchApiUrl()}?${params}`, {
        signal: this.abortController.signal,
      });

      if (requestId !== this.requestId) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Boost API responded with ${response.status}`);
      }

      const data = await response.json();

      if (requestId !== this.requestId) {
        return null;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      this.analyticsRequestId = data.meta?.rid || '';

      const filterOptions = this.getFilterOptions(data.filter);

      if (filterOptions.length) {
        this.hasFilterTree = true;
      }

      if (shouldRenderFilters && this.shouldRenderFilterTree(eventType)) {
        this.renderFilters(data.filter, { keepPrevious: filterOptions.length === 0 });
      }

      if (renderResults) {
        if (searchQuery) {
          this.renderResults(data);
        } else {
          this.clearResults({ keepFilters: true });
        }
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') return null;
      console.error('[boost-search]', error);
      if (renderResults) {
        this.renderError();
      }
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  loadFilters() {
    if (!this.isProductSearch()) {
      return Promise.resolve();
    }

    return this.fetchBoost({
      eventType: 'init',
      rebuildTree: true,
      renderResults: false,
      renderFilters: true,
      query: this.currentQuery,
    });
  }

  async search({ rebuildTree = true, eventType = 'filter', renderFilters, autoSelectTab = false, triedTypes = null } = {}) {
    const query = this.getQuery() || this.getQueryFromUrl();

    if (!query) {
      this.clearResults({ keepFilters: this.isProductSearch() });
      return null;
    }

    this.currentQuery = query;

    if (!triedTypes) {
      this.lastSearchTotals = null;
    }

    const tried = triedTypes || new Set();
    tried.add(this.currentSearchType);

    const deferRender = Boolean(autoSelectTab);
    let data = null;

    if (this.isPageSearch()) {
      data = await this.searchPages({ eventType, renderResults: !deferRender });
    } else if (this.isCollectionSearch()) {
      data = await this.searchCollections({ eventType, renderResults: !deferRender });
    } else {
      const shouldRenderFilters =
        renderFilters ?? (this.isProductSearch() && this.shouldRenderFilterTree(eventType));

      data = await this.fetchBoost({
        eventType,
        rebuildTree: rebuildTree !== false,
        renderResults: !deferRender,
        renderFilters: shouldRenderFilters,
        query,
      });
    }

    this.mergeSearchTotals(data);

    if (!autoSelectTab || !data) {
      return data;
    }

    if (this.getCurrentSearchResultCount(data) > 0) {
      this.renderDeferredResults(data);
      return data;
    }

    const nextType = await this.findNextTabWithResults(tried, data);
    if (!nextType) {
      this.renderDeferredResults(data);
      return data;
    }

    this.applySearchType(nextType, {
      keepPageCounts: nextType === 'pages' || nextType === 'pages-services',
    });
    this.syncTabState();
    this.updateUrl({ push: true });

    return this.search({
      rebuildTree: nextType === 'product',
      autoSelectTab: true,
      triedTypes: tried,
    });
  }

  async searchPages({ eventType = 'filter', renderResults = true } = {}) {
    let data = null;

    if (this.pageCountsCache?.query === this.currentQuery && this.pageCountsCache.data) {
      data = this.pageCountsCache.data;
    } else {
      data = await this.fetchAllBoostPages({
        eventType,
        query: this.currentQuery,
        searchType: 'pages',
      });
    }

    if (!data) return null;

    this.filteredPagesCache = this.filterPagesByBoostConfig(data.pages || []);
    this.lastPageSearchMeta = { typo: data.typo };
    this.pagesVisibleCount = this.limit;

    if (!this.pageCountsCache || this.pageCountsCache.query !== this.currentQuery) {
      this.pageCountsCache = {
        query: this.currentQuery,
        page: this.filterPagesByBoostConfig(data.pages || [], 'page').length,
        service: this.filterPagesByBoostConfig(data.pages || [], 'service').length,
        data,
      };
    }

    if (renderResults) {
      this.renderPageResultsFromCache();
    }

    return data;
  }

  async searchCollections({ eventType = 'filter', renderResults = true } = {}) {
    const data = await this.fetchAllBoostCollections({
      eventType,
      query: this.currentQuery,
      searchType: 'collections',
    });
    if (!data) return null;

    this.filteredCollectionsCache = this.filterVisibleCollections(data.collections || []);
    this.lastCollectionSearchMeta = { typo: data.typo };

    if (renderResults) {
      this.hideToolbar();
      this.renderCollectionResultsFromCache();
    }

    return data;
  }

  async fetchAllBoostCollections({ eventType = 'filter', query, searchType = 'collections' } = {}) {
    if (!this.shop) {
      console.error('[boost-search] Missing Boost shop domain in boostPFSConfig');
      return null;
    }

    const searchQuery = query ?? this.currentQuery;
    const fetchLimit = 70;
    const allCollections = [];
    let apiPage = 1;
    let totalCount = Infinity;
    let lastResponse = null;

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const requestId = ++this.requestId;

    this.setLoading(true);

    try {
      while (allCollections.length < totalCount) {
        const params = this.buildParams({
          eventType,
          rebuildTree: false,
          query: searchQuery,
          searchType,
        });
        params.set('limit', String(fetchLimit));
        params.set('page', String(apiPage));

        const response = await fetch(`${this.getSearchApiUrl(searchType)}?${params}`, {
          signal: this.abortController.signal,
        });

        if (requestId !== this.requestId) {
          return null;
        }

        if (!response.ok) {
          throw new Error(`Boost API responded with ${response.status}`);
        }

        const data = await response.json();
        lastResponse = data;

        if (requestId !== this.requestId) {
          return null;
        }

        if (data.error) {
          throw new Error(data.error);
        }

        const collections = data.collections || [];
        allCollections.push(...collections);
        totalCount = data.total_collection ?? allCollections.length;

        if (!collections.length || allCollections.length >= totalCount) {
          break;
        }

        apiPage += 1;
      }

      return {
        ...lastResponse,
        collections: allCollections,
        total_collection: allCollections.length,
      };
    } catch (error) {
      if (error.name === 'AbortError') return null;
      console.error('[boost-search]', error);
      this.renderError();
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  async fetchAllBoostPages({ eventType = 'filter', query, searchType = 'pages', renderErrorOnFail = true } = {}) {
    if (!this.shop) {
      console.error('[boost-search] Missing Boost shop domain in boostPFSConfig');
      return null;
    }

    const searchQuery = query ?? this.currentQuery;
    const fetchLimit = 70;
    const allPages = [];
    let apiPage = 1;
    let totalCount = Infinity;
    let lastResponse = null;

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const requestId = ++this.requestId;

    this.setLoading(true);

    try {
      while (allPages.length < totalCount) {
        const params = this.buildParams({
          eventType,
          rebuildTree: false,
          query: searchQuery,
          searchType,
        });
        params.set('limit', String(fetchLimit));
        params.set('page', String(apiPage));

        const response = await fetch(`${this.getSearchApiUrl(searchType)}?${params}`, {
          signal: this.abortController.signal,
        });

        if (requestId !== this.requestId) {
          return null;
        }

        if (!response.ok) {
          throw new Error(`Boost API responded with ${response.status}`);
        }

        const data = await response.json();
        lastResponse = data;

        if (requestId !== this.requestId) {
          return null;
        }

        if (data.error) {
          throw new Error(data.error);
        }

        const pages = data.pages || [];
        allPages.push(...pages);
        totalCount = data.total_page ?? allPages.length;

        if (!pages.length || allPages.length >= totalCount) {
          break;
        }

        apiPage += 1;
      }

      return {
        ...lastResponse,
        pages: allPages,
        total_page: allPages.length,
      };
    } catch (error) {
      if (error.name === 'AbortError') return null;
      console.error('[boost-search]', error);
      if (renderErrorOnFail) {
        this.renderError();
      }
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  goToPage(pageIndex) {
    const totalItems = this.lastTotalItems || 0;
    const totalPages = Math.ceil(totalItems / this.limit);

    if (pageIndex < 0 || pageIndex >= totalPages) {
      return;
    }

    this.currentPage = pageIndex;
    this.updateUrl({ push: true });

    if (this.isCollectionSearch() && this.filteredCollectionsCache) {
      this.renderCollectionResultsFromCache();
      this.scrollToResults();
      return;
    }

    this.search({ rebuildTree: false, eventType: 'page', renderFilters: false }).then(() => {
      this.scrollToResults();
    });
  }

  onPaginationClick(event) {
    const button = event.target.closest('[data-boost-page]');
    if (!button || button.disabled) return;

    const action = button.dataset.boostPage;

    if (action === 'next') {
      this.goToPage(this.currentPage + 1);
      return;
    }

    if (action === 'prev') {
      this.goToPage(this.currentPage - 1);
      return;
    }

    const pageIndex = parseInt(action, 10);
    if (Number.isFinite(pageIndex)) {
      this.goToPage(pageIndex);
    }
  }

  getPaginationItems(totalPages) {
    if (totalPages <= 1) {
      return [];
    }

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const current = this.currentPage + 1;
    const items = [1];

    let start = Math.max(2, current - 1);
    let end = Math.min(totalPages - 1, current + 2);

    if (current <= 3) {
      start = 2;
      end = Math.min(3, totalPages - 1);
    } else if (current >= totalPages - 2) {
      start = Math.max(2, totalPages - 3);
      end = totalPages - 1;
    }

    if (start > 2) {
      items.push('ellipsis');
    }

    for (let page = start; page <= end; page += 1) {
      items.push(page);
    }

    if (end < totalPages - 1) {
      items.push('ellipsis');
    }

    items.push(totalPages);
    return items;
  }

  renderPaginationArrow() {
    return `
      <svg class="boost-search__pagination-icon" viewBox="0 0 21 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 10H18M18 10L12.1667 4.16675M18 10L12.1667 15.8334"></path>
      </svg>
    `;
  }

  renderPaginationItem(item) {
    if (item === 'ellipsis') {
      return `<span class="boost-search__pagination-ellipsis" aria-hidden="true">…</span>`;
    }

    const pageIndex = item - 1;
    const isCurrent = pageIndex === this.currentPage;

    if (isCurrent) {
      return `
        <span class="boost-search__pagination-page is-current" aria-current="page">
          ${item}
        </span>
      `;
    }

    return `
      <button
        type="button"
        class="boost-search__pagination-page"
        data-boost-page="${pageIndex}"
        aria-label="Page ${item}"
      >
        ${item}
      </button>
    `;
  }

  scrollToResults() {
    this.resultsContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  renderPagination(totalItems) {
    if (totalItems <= this.limit) {
      return '';
    }

    const totalPages = Math.ceil(totalItems / this.limit);
    const items = this.getPaginationItems(totalPages);
    const prevLabel = this.dataset.paginationPrev || 'Previous';
    const nextLabel = this.dataset.paginationNext || 'Next';
    const ariaLabel = this.dataset.paginationLabel || 'Pagination';
    const hasPrev = this.currentPage > 0;
    const hasNext = this.currentPage < totalPages - 1;

    return `
      <nav class="boost-search__pagination" aria-label="${this.escapeHtml(ariaLabel)}">
        ${
          hasPrev
            ? `
          <button
            type="button"
            class="boost-search__pagination-prev"
            data-boost-page="prev"
            aria-label="${this.escapeHtml(prevLabel)}"
          >
            ${this.renderPaginationArrow()}
            <span class="boost-search__pagination-prev-label">${this.escapeHtml(prevLabel)}</span>
          </button>
        `
            : ''
        }
        ${items.map((item) => this.renderPaginationItem(item)).join('')}
        ${
          hasNext
            ? `
          <button
            type="button"
            class="boost-search__pagination-next"
            data-boost-page="next"
            aria-label="${this.escapeHtml(nextLabel)}"
          >
            <span class="boost-search__pagination-next-label">${this.escapeHtml(nextLabel)}</span>
            ${this.renderPaginationArrow()}
          </button>
        `
            : ''
        }
      </nav>
    `;
  }

  searchProducts(options = {}) {
    return this.search(options);
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (this.input) {
      this.input.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    const drawer = this.filtersDrawer;
    if (!drawer) return;

    if (isLoading && drawer.hasAttribute('open')) {
      drawer.classList.add('loading');
    } else {
      drawer.classList.remove('loading');
    }
  }

  clearResults({ keepFilters = false } = {}) {
    this.disconnectInfiniteScroll();

    if (!keepFilters) {
      this.hasFilterTree = false;

      if (this.filtersDrawerContent) {
        this.filtersDrawerContent.innerHTML = '';
      }

      if (this.filtersTrigger) {
        this.filtersTrigger.innerHTML = '';
        this.filtersTrigger.hidden = true;
      }
    }

    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
    }

    this.hideToolbar();
    this.hideSearchInfo();
  }

  hideToolbar() {
    if (this.toolbar) {
      this.toolbar.classList.add('hidden');
      this.toolbar.hidden = true;
    }

    if (this.countElement) {
      this.countElement.textContent = '';
    }

    if (this.sortingContainer) {
      this.sortingContainer.innerHTML = '';
    }

    if (this.filtersTrigger) {
      this.filtersTrigger.innerHTML = '';
      this.filtersTrigger.hidden = true;
    }

    this.lastTotalItems = 0;
  }

  renderSortCloseIcon() {
    return `
      <svg class="icon icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
      </svg>
    `;
  }

  renderSortSelectionIcon() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
        <path d="M6.70801 4.3125V18.6875M9.58301 7.1875L6.70801 4.3125L3.83301 7.1875" stroke="#1D1D1F" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M16.292 18.6875V4.3125M19.167 15.8125L16.292 18.6875L13.417 15.8125" stroke="#1D1D1F" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  renderSorting() {
    const container = this.sortingContainer;
    if (!container || !this.isProductSearch()) {
      return;
    }

    const options = this.getSortOptions();
    const sortByLabel = this.dataset.sortByLabel || 'Sort by:';
    const selectedLabel = this.escapeHtml(this.getSortLabel());
    const key = this.handleize(selectedLabel);
    const label = window.filter_values[key] || selectedLabel;

    container.innerHTML = `
      <div class="facet-sorting inline-flex items-center gap-6" id="SortByContainer">
        <facet-sort class="sort-wrapper relative">
          <hover-element class="button button--third sort-by whitespace-nowrap overflow-hidden z-3">
            <span class="btn-fill" data-fill></span>
            <div class="btn-text">
              <span class="sort-header flex items-center justify-center gap-1 relative w-full">
                <span class="niccos-sort-label">${this.escapeHtml(sortByLabel)}</span>
                <span class="sort-selection">${label}</span>
                <span class="sort-close flex items-center justify-center rounded-full">
                  ${this.renderSortCloseIcon()}
                </span>
                <span class="sort-mobile-icon">
                  ${this.renderSortSelectionIcon()}
                </span>

              </span>
              <ul class="sort-listbox text-left grid gap-2 absolute left-0 opacity-0 invisible w-full h-auto">
                ${options
                  .map((option) => {
                    const checked = option.value === this.currentSort ? 'checked' : '';
                    const inputId = `SortBy-boost-${option.value}`;
                    const key = this.handleize(option.name);
                    const label = window.filter_values[key] || option.name;

                    return `
                      <li class="opacity-0 relative">
                        <input
                          id="${this.escapeHtml(inputId)}"
                          class="sr-only"
                          name="sort_by"
                          type="radio"
                          value="${this.escapeHtml(option.value)}"
                          ${checked}
                        >
                        <label for="${this.escapeHtml(inputId)}" class="reversed-link text-xl">${this.escapeHtml(label)}</label>
                      </li>
                    `;
                  })
                  .join('')}
              </ul>
            </div>
          </hover-element>
        </facet-sort>
      </div>
    `;
  }

  onSortChange(event) {
    const input = event.target;

    if (!input.matches('input[name="sort_by"]') || !input.checked) {
      return;
    }

    const nextSort = input.value;

    if (nextSort === this.currentSort) {
      return;
    }

    this.currentSort = nextSort;
    this.currentPage = 0;
    this.updateUrl({ push: true });
    this.search({ rebuildTree: false, eventType: 'sort', renderFilters: false });
  }

  renderFilters(filter, { keepPrevious = false } = {}) {
    const container = this.filtersDrawerContent;
    if (!container || !this.isProductSearch()) return;

    const drawer = this.filtersDrawer;
    const wasOpen = drawer?.hasAttribute('open');

    let options = this.normalizeFilterOptions(this.getFilterOptions(filter));

    if (!options.length && keepPrevious && this.lastFilterOptions?.length) {
      options = this.normalizeFilterOptions(this.lastFilterOptions);
    }

    if (!options.length) {
      if (!this.lastFilterOptions?.length) {
        container.innerHTML = '';
      }

      this.renderFilterTrigger();
      this.updateFilterCounts();
      this.updateToolbarVisibility();
      return;
    }

    this.lastFilterOptions = options;
    container.innerHTML = `
      <div class="boost-search__facet-form">
        ${options.map((option, index) => this.renderFilterOption(option, index)).join('')}
      </div>
    `;

    this.renderFilterTrigger();
    this.updateFilterCounts();
    this.updateToolbarVisibility();

    if (wasOpen && drawer) {
      drawer.setAttribute('open', '');
      drawer.removeAttribute('hidden');
    }
  }

  renderFilterOption(option, index) {
    const optionId = option.filterOptionId;
    const filterId = `boost-filter-${index}-${optionId}`;


    if (this.isRangeFilter(option)) {
      return this.renderRangeFilterOption(option, filterId);
    }

    const selected = this.selectedFilters.get(optionId) || new Set();
    const isMultiple = option.selectType === 'multiple';
    const inputType = isMultiple ? 'checkbox' : 'radio';
    const values = this.getFilterValues(option);
    const key = this.handleize(option.label);
    const label = window.filter_values[key] || option.label;

    return `
      <details class="details active" is="accordion-details" open data-filter data-index="${this.escapeHtml(filterId)}">
        <summary class="details__summary flex items-center justify-between gap-2 cursor-pointer">
          <span class="text-base-xl font-medium">${this.escapeHtml(label)}</span>
          ${this.renderChevronIcon()}
        </summary>
        <div class="details__content">
          <ul class="list grid gap-3" role="list">
            ${values
              .map((value, valueIndex) => {
                const paramValue = this.getFilterValue(value);
                const key = this.handleize(paramValue);
                const checked = selected.has(paramValue) ? 'checked' : '';
                const label = window.filter_values[key] || paramValue;
                const count = value.doc_count ?? 0;
                const isDisabled = count === 0 && !checked;
                const inputId = `${filterId}-${valueIndex}`;
                const inputClass = inputType === 'checkbox' ? 'checkbox' : 'radio';

                return `
                  <li class="flex items-center gap-3">
                    <input
                      type="${inputType}"
                      class="${inputClass}"
                      name="${this.escapeHtml(optionId)}"
                      id="${this.escapeHtml(inputId)}"
                      value="${this.escapeHtml(paramValue)}"
                      data-filter-option="${this.escapeHtml(optionId)}"
                      ${checked}
                      ${isDisabled ? 'disabled' : ''}
                    >
                    <label for="${this.escapeHtml(inputId)}" class="flex items-baseline gap-1">
                      <span>${this.escapeHtml(label)}<span data-count="${count}" class="filter-qnt-item text-sm${checked ? '' : ' text-opacity'}">(${count})</span></span>
                    </label>
                  </li>
                `;
              })
              .join('')}
          </ul>
        </div>
      </details>
    `;
  }

  renderRangeFilterOption(option, filterId) {
    const optionId = option.filterOptionId;
    const bounds = this.getRangeBounds(option);
    if (!bounds) return '';

    const range = this.getSelectedRange(optionId, bounds);
    const rangeMax = bounds.max;
    const minValue = range.min;
    const maxValue = range.max;
    const stepValue = Math.max(1, Number(option.sliderStep) || 1);
    const lowerBoundProgress = rangeMax > 0 ? (minValue / rangeMax) * 100 : 0;
    const higherBoundProgress = rangeMax > 0 ? (maxValue / rangeMax) * 100 : 100;
    const currencySymbol = this.dataset.currencySymbol || '';
    const fromLabel = this.dataset.fromPriceLabel || 'From';
    const toLabel = this.dataset.toPriceLabel || 'To';
    const rangeFromId = `FacetFormRangeFromPrice-${filterId}`;
    const rangeToId = `FacetFormRangeToPrice-${filterId}`;
    const inputFromId = `FacetFormInputFromPrice-${filterId}`;
    const inputToId = `FacetFormInputToPrice-${filterId}`;
    const hideTextBoxes = option.hideTextBoxes === true;
    const key = this.handleize(option.label);
    const label = window.filter_values[key] || option.label;

    return `
      <details class="details active" is="accordion-details" open data-filter data-index="${this.escapeHtml(filterId)}">
        <summary class="details__summary flex items-center justify-between gap-2 cursor-pointer">
          <span class="text-base-xl font-medium">${label}</span>
          ${this.renderChevronIcon()}
        </summary>
        <div class="details__content">
          <price-range
            class="price-range grid gap-5"
            data-filter-option="${this.escapeHtml(optionId)}"
            data-range-min="0"
            data-range-max="${rangeMax}"
          >
            <label class="sr-only" for="${this.escapeHtml(rangeFromId)}">${this.escapeHtml(fromLabel)}</label>
            <label class="sr-only" for="${this.escapeHtml(rangeToId)}">${this.escapeHtml(toLabel)}</label>
            <div class="range-wrapper relative" style="--range-min: ${lowerBoundProgress}%; --range-max: ${higherBoundProgress}%">
              <input class="range w-full pointer-events-none" type="range"
                min="0"
                max="${rangeMax}"
                step="${stepValue}"
                value="${minValue}"
                id="${this.escapeHtml(rangeFromId)}"
              />
              <input class="range w-full pointer-events-none absolute top-0 left-0" type="range"
                min="0"
                max="${rangeMax}"
                step="${stepValue}"
                value="${maxValue}"
                id="${this.escapeHtml(rangeToId)}"
              />
            </div>
            <div class="input-wrapper flex items-center"${hideTextBoxes ? ' hidden' : ''}>
              <div class="field flex items-center relative">
                <label class="sr-only" for="${this.escapeHtml(inputFromId)}">${this.escapeHtml(fromLabel)}</label>
                <span class="prefix absolute left-0">${this.escapeHtml(currencySymbol)}</span>
                <input class="input" type="number" inputmode="numeric"
                  name="filter.v.price.gte"
                  id="${this.escapeHtml(inputFromId)}"
                  autocomplete="off"
                  placeholder="0"
                  min="0"
                  max="${maxValue}"
                  step="${stepValue}"
                  data-filter-option="${this.escapeHtml(optionId)}"
                  data-price-bound="min"
                  ${range.isActive ? `value="${minValue}"` : ''}
                />
              </div>
              <span>-</span>
              <div class="field flex items-center relative">
                <label class="sr-only" for="${this.escapeHtml(inputToId)}">${this.escapeHtml(toLabel)}</label>
                <span class="prefix absolute left-0">${this.escapeHtml(currencySymbol)}</span>
                <input class="input" type="number" inputmode="numeric"
                  name="filter.v.price.lte"
                  id="${this.escapeHtml(inputToId)}"
                  autocomplete="off"
                  placeholder="${rangeMax}"
                  min="${minValue}"
                  max="${rangeMax}"
                  step="${stepValue}"
                  data-filter-option="${this.escapeHtml(optionId)}"
                  data-price-bound="max"
                  ${range.isActive ? `value="${maxValue}"` : ''}
                />
              </div>
            </div>
          </price-range>
        </div>
      </details>
    `;
  }

  getFilterValue(value) {
    if (value.from != null || value.to != null) {
      return `${value.from ?? ''}:${value.to ?? ''}`;
    }

    return String(value.key ?? value.value ?? value.label ?? '');
  }

  onPriceRangeChange(optionId, input) {
    const container = input.closest('price-range');
    const minInput = container?.querySelector('[data-price-bound="min"]');
    const maxInput = container?.querySelector('[data-price-bound="max"]');
    const absoluteMin = Number(container?.dataset.rangeMin ?? 0);
    const absoluteMax = Number(container?.dataset.rangeMax ?? 0);

    const minValue =
      minInput?.value !== '' && Number.isFinite(Number(minInput.value))
        ? Math.ceil(Number(minInput.value))
        : absoluteMin;
    const maxValue =
      maxInput?.value !== '' && Number.isFinite(Number(maxInput.value))
        ? Math.ceil(Number(maxInput.value))
        : absoluteMax;

    const nextMin = Math.min(Math.max(absoluteMin, minValue), absoluteMax);
    const nextMax = Math.max(Math.min(absoluteMax, maxValue), absoluteMin);

    if (nextMin <= absoluteMin && nextMax >= absoluteMax) {
      this.selectedFilters.delete(optionId);
    } else {
      this.selectedFilters.set(optionId, new Set([`${nextMin}:${nextMax}`]));
    }

    this.currentPage = 0;
    this.invalidatePageCache();
    this.updateUrl({ push: true });
    this.updateFilterCounts();
    this.search({ rebuildTree: true, eventType: 'filter' });
  }

  onFilterChange(event) {
    const input = event.target;
    const optionId = input.dataset.filterOption;

    if (!optionId) return;

    if (input.dataset.priceBound) {
      this.onPriceRangeChange(optionId, input);
      return;
    }

    const value = input.value;

    if (input.type === 'radio') {
      if (input.checked) {
        this.selectedFilters.set(optionId, new Set([value]));
      } else {
        this.selectedFilters.delete(optionId);
      }
    } else if (input.checked) {
      if (!this.selectedFilters.has(optionId)) {
        this.selectedFilters.set(optionId, new Set());
      }
      this.selectedFilters.get(optionId).add(value);
    } else {
      const values = this.selectedFilters.get(optionId);
      values?.delete(value);
      if (!values?.size) {
        this.selectedFilters.delete(optionId);
      }
    }

    this.currentPage = 0;
    this.invalidatePageCache();
    this.updateUrl({ push: true });
    this.updateFilterCounts();
    this.search({ rebuildTree: true, eventType: 'filter' });
  }

  renderResults(data) {
    switch (this.currentSearchType) {
      case 'collections':
        this.hideToolbar();
        this.renderCollectionResults(data);
        break;
      case 'pages':
      case 'pages-services':
        this.hideToolbar();
        this.renderPageResults(data);
        break;
      default:
        this.renderProductResults(data);
    }

    this.updateSearchInfo();
  }

  getCountLabel(total, searchType = this.currentSearchType) {
    const labels = {
      product: {
        singular: this.dataset.productLabel || 'product',
        plural: this.dataset.productsLabel || 'products',
      },
      collections: {
        singular: this.dataset.collectionLabel || 'collection',
        plural: this.dataset.collectionsLabel || 'collections',
      },
      pages: {
        singular: this.dataset.pageLabel || 'page',
        plural: this.dataset.pagesLabel || 'pages',
      },
      'pages-services': {
        singular: this.dataset.serviceLabel || 'service',
        plural: this.dataset.servicesLabel || 'services',
      },
    };

    const { singular, plural } = labels[searchType] || labels.product;
    return total === 1 ? singular : plural;
  }

  renderProductResults(data) {
    const container = this.resultsContainer;
    if (!container) return;

    const products = data.products || [];
    const total = data.total_product ?? products.length;
    this.lastTotalItems = total;
    const typo = data.typo;
    const resultsType = this.getCurrentResultsType();

    if (!products.length) {
      if (this.countElement) {
        this.countElement.textContent = `0 ${this.getCountLabel(0, 'product')}`;
      }

      this.renderFilterTrigger();
      this.updateFilterCounts();
      this.updateToolbarVisibility();

      container.innerHTML = this.renderEmptyState(this.dataset.emptyText || 'No products found', 'product');
      return;
    }

    const typoHtml = typo?.correctQuery
      ? `<p class="boost-search__typo">Showing results for "${this.escapeHtml(typo.correctQuery)}"</p>`
      : '';

    if (this.countElement) {
      this.countElement.textContent = `${total} ${this.getCountLabel(total, 'product')}`;
    }

    this.renderSorting();
    this.renderFilterTrigger();
    this.updateFilterCounts();
    this.updateToolbarVisibility();

    container.innerHTML = `
      ${typoHtml}
      <ul class="boost-search__products" data-results-type="${this.escapeHtml(resultsType)}" role="list">
        ${products.map((product) => this.renderProduct(product, resultsType)).join('')}
      </ul>
      ${this.renderPagination(total)}
    `;

  }

  renderCollectionResults(data) {
    this.filteredCollectionsCache = this.filterVisibleCollections(data.collections || []);
    this.lastCollectionSearchMeta = { typo: data.typo };
    this.renderCollectionResultsFromCache();
  }

  renderCollectionResultsFromCache() {
    const container = this.resultsContainer;
    if (!container) return;

    const filteredCollections = this.filteredCollectionsCache || [];
    const total = filteredCollections.length;
    this.lastTotalItems = total;
    const typo = this.lastCollectionSearchMeta?.typo;
    const emptyText = this.dataset.emptyCollectionsText || 'No collections found';
    const start = this.currentPage * this.limit;
    const collections = filteredCollections.slice(start, start + this.limit);
    const resultsType = this.getCurrentResultsType();

    if (!filteredCollections.length) {
      container.innerHTML = this.renderEmptyState(emptyText, 'collections');
      this.updateSearchInfo(0);
      return;
    }

    const typoHtml = typo?.correctQuery
      ? `<p class="boost-search__typo">Showing results for "${this.escapeHtml(typo.correctQuery)}"</p>`
      : '';

    container.innerHTML = `
      ${typoHtml}
      <h2 class="boost-search__count">${total} ${this.escapeHtml(this.getCountLabel(total, 'collections'))}</h2>
      <ul class="boost-search__collections" data-results-type="${this.escapeHtml(resultsType)}" role="list">
        ${collections.map((collection) => this.renderCollection(collection, resultsType)).join('')}
      </ul>
      ${this.renderPagination(total)}
    `;

    this.updateSearchInfo(total);
  }

  renderCollection(collection, resultsType = this.getCurrentResultsType()) {
    const image = collection.image?.src || '';
    const url = `${this.rootUrl && this.rootUrl !== '/' ? this.rootUrl : ''}/collections/${collection.handle}`;


    let collectionContent = '';
    switch (this.escapeHtml(resultsType)) {
      case 'card':
        collectionContent = `
          <li class="boost-search__collection">
            <a class="boost-search__collection-link" href="${this.escapeHtml(url)}">
              ${
                image
                  ? `<img class="boost-search__collection-image" src="${this.escapeHtml(image)}" alt="${this.escapeHtml(collection.title)}" loading="lazy" width="200" height="200">`
                  : this.placeholder?.innerHTML || ''
              }
              <h3 class="boost-search__collection-title">${this.escapeHtml(collection.title)}</h3>
            </a>
          </li>
        `;
        break;
      case 'list':
      case 'no-image-card':
        collectionContent = `
          <li class="boost-search__collection">
            <a class="boost-search__collection-link" href="${this.escapeHtml(url)}">
              <h3 class="boost-search__collection-title">${this.escapeHtml(collection.title)}</h3>
            </a>
          </li>
        `;
        break;
    }

    return collectionContent;
  }

  getBoostPages() {
    return Array.isArray(window.boostPages) ? window.boostPages : [];
  }

  getHiddenCollections() {
    return window.hiddenCollections && typeof window.hiddenCollections === 'object'
      ? window.hiddenCollections
      : {};
  }

  isHiddenCollection(id) {
    if (id == null || id === '') return false;
    return Boolean(this.getHiddenCollections()[Number(id)]);
  }

  filterVisibleCollections(collections) {
    return (collections || []).filter(
      (collection) => collection?.id != null && !this.isHiddenCollection(collection.id)
    );
  }

  getBoostPageTypeForSearch() {
    return this.currentSearchType === 'pages-services' ? 'service' : 'page';
  }

  filterPagesByBoostConfig(pages, type = this.getBoostPageTypeForSearch()) {
    const targetType = type;
    const configById = new Map(
      this.getBoostPages()
        .filter((entry) => entry?.id != null && entry.type === targetType)
        .map((entry) => [Number(entry.id), entry])
    );

    return pages
      .filter((page) => page?.id != null && configById.has(Number(page.id)))
      .map((page) => ({
        page,
        boostPage: configById.get(Number(page.id)),
      }));
  }

  renderPageResults(data) {
    this.filteredPagesCache = this.filterPagesByBoostConfig(data.pages || []);
    this.lastPageSearchMeta = { typo: data.typo };
    this.pagesVisibleCount = this.limit;
    this.renderPageResultsFromCache();
  }

  renderPageResultsFromCache() {
    const container = this.resultsContainer;
    if (!container) return;

    this.disconnectInfiniteScroll();

    const filteredPages = this.filteredPagesCache || [];
    const total = filteredPages.length;
    this.lastTotalItems = total;
    const typo = this.lastPageSearchMeta?.typo;
    const emptyText = this.dataset.emptyPagesText || 'No pages found';
    const pageItems = filteredPages.slice(0, this.pagesVisibleCount);
    const hasMore = this.pagesVisibleCount < total;
    const resultsType = this.getCurrentResultsType();

    if (!filteredPages.length) {
      container.innerHTML = this.renderEmptyState(emptyText);
      this.updateSearchInfo(0);
      return;
    }

    const typoHtml = typo?.correctQuery
      ? `<p class="boost-search__typo">Showing results for "${this.escapeHtml(typo.correctQuery)}"</p>`
      : '';

    container.innerHTML = `
      ${typoHtml}
      <h2 class="boost-search__count">${total} ${this.escapeHtml(this.getCountLabel(total))}</h2>
      <ul class="boost-search__pages" data-results-type="${this.escapeHtml(resultsType)}" role="list">
        ${pageItems.map(({ page, boostPage }) => this.renderPage(page, boostPage, resultsType)).join('')}
      </ul>
      ${hasMore ? this.renderInfiniteScrollSentinel() : ''}
    `;

    if (hasMore) {
      requestAnimationFrame(() => this.setupInfiniteScroll());
    }

    this.updateSearchInfo(total);
  }

  renderInfiniteScrollSentinel() {
    return `
      <div class="boost-search__infinite-sentinel" data-boost-infinite-sentinel aria-hidden="true">
        <span class="boost-search__infinite-loader"></span>
      </div>
    `;
  }

  setupInfiniteScroll() {
    this.disconnectInfiniteScroll();

    const sentinel = this.resultsContainer?.querySelector('[data-boost-infinite-sentinel]');
    if (!sentinel) return;

    this.infiniteObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMorePageResults();
        }
      },
      { rootMargin: '240px' }
    );

    this.infiniteObserver.observe(sentinel);
  }

  disconnectInfiniteScroll() {
    this.infiniteObserver?.disconnect();
    this.infiniteObserver = null;
    this.isLoadingMorePages = false;
  }

  loadMorePageResults() {
    const filteredPages = this.filteredPagesCache || [];

    if (this.isLoadingMorePages || this.pagesVisibleCount >= filteredPages.length) {
      return;
    }

    this.isLoadingMorePages = true;

    const prevCount = this.pagesVisibleCount;
    this.pagesVisibleCount = Math.min(prevCount + this.limit, filteredPages.length);

    const newItems = filteredPages.slice(prevCount, this.pagesVisibleCount);
    const list = this.resultsContainer?.querySelector('.boost-search__pages');

    if (list && newItems.length) {
      const resultsType = this.getCurrentResultsType();
      list.insertAdjacentHTML(
        'beforeend',
        newItems.map(({ page, boostPage }) => this.renderPage(page, boostPage, resultsType)).join('')
      );
    }

    const hasMore = this.pagesVisibleCount < filteredPages.length;
    const sentinel = this.resultsContainer?.querySelector('[data-boost-infinite-sentinel]');

    if (!hasMore) {
      sentinel?.remove();
      this.disconnectInfiniteScroll();
    } else if (sentinel) {
      this.infiniteObserver?.unobserve(sentinel);
      this.infiniteObserver?.observe(sentinel);
    }

    this.isLoadingMorePages = false;
  }

  renderPage(page, boostPage = null, resultsType = this.getCurrentResultsType()) {
    const url = `${this.rootUrl && this.rootUrl !== '/' ? this.rootUrl : ''}${page.url}`;
    const image = boostPage?.image || '';

    let pageContent = '';
    switch (this.escapeHtml(resultsType)) {
      case 'card':
        pageContent = `
          <li class="boost-search__page">
            <a class="boost-search__page-link" href="${this.escapeHtml(url)}">
              ${
                image
                  ? `<img class="boost-search__page-image" src="${this.escapeHtml(image)}" alt="${this.escapeHtml(page.title)}" loading="lazy" width="200" height="200">`
                  : this.placeholder?.innerHTML || ''
              }
              <h3 class="boost-search__page-title">${this.escapeHtml(page.title)}</р>
            </a>
          </li>
        `;
        break;
      case 'list':
      case 'no-image-card':
        pageContent = `
          <li class="boost-search__page">
            <a class="boost-search__page-link" href="${this.escapeHtml(url)}">
              <h3 class="boost-search__page-title">${this.escapeHtml(page.title)}</h3>
            </a>
          </li>
        `;
        break;
    }

    return pageContent;
  }

  renderProductRating(product) {
    const count = Number(product.review_count) || 0;
    if (count <= 0) return '';

    const rating = Math.round((Number(product.review_ratings) || 0) * 100) / 100;
    const ratingMax = 5;
    const percent = Math.min(100, Math.max(0, (rating / ratingMax) * 100));
    const ratingLabel = rating.toFixed(2);
    const ratingMaxLabel = ratingMax.toFixed(2);
    const text = this.dataset.reviewsText || 'Bewertungen';

    return `
      <div
        class="boost-search__rating"
        role="img"
        aria-label="${count} ${this.escapeHtml(text)}, ${ratingLabel} / ${ratingMaxLabel}"
      >
        <span class="boost-search__rating-stars" style="--rating-percent: ${percent}%" aria-hidden="true"></span>
        <span class="boost-search__rating-count" aria-hidden="true">${count}</span>
        <span class="boost-search__rating-text" aria-hidden="true">${this.escapeHtml(text)}</span>
        <span class="boost-search__rating-value" aria-hidden="true"><span class="boost-search__rating_number">${ratingLabel} </span>/ ${ratingMaxLabel}</span>
      </div>
    `;
  }

  getBadges(metafields) {
    const badgesObject = metafields?.find(
      obj => obj.key === "boost_badges" && obj.namespace === "n8n"
    );

    return badgesObject ? JSON.parse(badgesObject.value) : [];
  }

  renderBadges(badges) {
    const marketMap = {
      austria: "AT",
      belgium: "BE",
      de: "DE",
      france: "FR",
      italy: "IT",
      luxembourg: "LU",
      netherlands: "NL",
      poland: "PL",
      spain: "ES",
      switzerland: "CH"
    };

    const currentMarket = marketMap[window.theme.currentMarket];

    const seenIds = new Set();

    const activeBadges = (badges || []).filter(badge => {
      if (
        badge.is_active !== "true" ||
        !badge.display_on_elements?.includes("Product Card") ||
        !badge.enabled_on_markets?.includes(currentMarket) ||
        seenIds.has(badge.id)
      ) {
        return false;
      }

      seenIds.add(badge.id);
      return true;
    }).slice(0, 2);

    if (!activeBadges.length) {
      return "";
    }

    return `
      <ul class="badges-block">
        ${activeBadges.map(badge => `
          <li
            class="badges-block__badge"
            style="--badge-bg-color: ${badge.badge_background_color?.split("|")[1]?.trim() ?? ""}; --badge-text-color: ${badge.badge_text_color?.split("|")[1]?.trim() ?? ""}"
          >
            ${badge.badge_content}
          </li>
        `).join("")}
      </ul>
    `;
  }

  renderProduct(product, resultsType = this.getCurrentResultsType()) {
    const badges = this.getBadges(product.metafields);
    const imageInfo = product.images_info?.[0] || {};
    const image = imageInfo.src || product.images?.['1'] || '';
    const imageAlt =
      imageInfo.alt ||
      imageInfo.alt_text ||
      product.image_alt ||
      product.imageAlt ||
      product.featured_image?.alt ||
      product.title ||
      '';
    const url = `${this.rootUrl && this.rootUrl !== '/' ? this.rootUrl : ''}/products/${product.handle}`;
    const price = this.formatPrice(product.price_min, product.price_max);
    const kiGeneratedBadge = this.renderKiGeneratedBadge(imageAlt);
    let compareAtPrice = '';
    if (product.compare_at_price_max && product.compare_at_price_max > product.price_min) {
      compareAtPrice = this.formatPrice(product.compare_at_price_max, product.compare_at_price_max);
    }

    let productContent = '';
    switch (this.escapeHtml(resultsType)) {
      case 'card':
        productContent = `
          <li class="boost-search__product">
            <a
              class="boost-search__product-link"
              href="${this.escapeHtml(url)}"
              data-boost-product-id="${this.escapeHtml(product.id)}"
              data-boost-request-id="${this.escapeHtml(this.analyticsRequestId || '')}"
            >
              ${
                image
                  ? `<span class="boost-search__product-media"><img class="boost-search__product-image" src="${this.escapeHtml(image)}" alt="${this.escapeHtml(imageAlt)}" loading="lazy" width="400" height="400">${kiGeneratedBadge}${badges.length > 0 ? this.renderBadges(badges) : ''}</span>`
                  : this.placeholder?.innerHTML || ''
              }
              ${this.renderProductRating(product)}
              <h3 class="boost-search__product-title">${this.escapeHtml(product.title)}</h3>
              <div class="boost-search__product-price-container">
                ${price ? `<span class="boost-search__product-price">${price}</span>` : ''}
                ${compareAtPrice != '' ? `<span class="boost-search__product-compare-price">${compareAtPrice}</span>` : ''}
              </div>
            </a>
          </li>
        `;
        break;
      case 'list':
      case 'no-image-card':
        productContent = `
          <li class="boost-search__product">
            <a
              class="boost-search__product-link"
              href="${this.escapeHtml(url)}"
              data-boost-product-id="${this.escapeHtml(product.id)}"
              data-boost-request-id="${this.escapeHtml(this.analyticsRequestId || '')}"
            >
              <h3 class="boost-search__product-title">${this.escapeHtml(product.title)}</h3>
            </a>
          </li>
        `;
        break;
    }

    return productContent
  }

  renderKiGeneratedBadge(alt) {
    const label = this.dataset.kiGeneratedLabel || '';
    const variant = this.getKiGeneratedVariant(alt);

    if (!label) return '';
    if (!variant) return '';

    return `<span class="ki-generated-badge ki-generated-badge--${variant}" data-ki-generated-badge data-ki-generated-variant="${variant}">
      <span class="ki-generated-badge__label">${this.escapeHtml(label)}</span>
    </span>`;
  }

  getKiGeneratedVariant(alt) {
    const normalizedAlt = String(alt || '').trim().toLowerCase();

    if (normalizedAlt.includes('ai generated')) return 'video';
    if (normalizedAlt.includes('ai black')) return 'light';
    if (normalizedAlt.includes('ai white')) return 'dark';

    return '';
  }

  formatPrice(priceMin, priceMax) {
    if (priceMin == null) return '';

    const format = (amount) => {
      if (window.theme?.Currency?.formatMoney) {
        return theme.Currency.formatMoney(Math.round(amount * 100), theme.settings.moneyFormat);
      }
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.currency || getBoostConfig().shop?.currency || 'EUR',
      }).format(amount);
    };

    if (priceMax != null && priceMax !== priceMin) {
      return `${format(priceMin)} – ${format(priceMax)}`;
    }

    return format(priceMin);
  }

  renderError() {
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = `
      <p class="boost-search__error">${this.escapeHtml(this.dataset.errorText || 'Search is temporarily unavailable.')}</p>
    `;
    this.hideSearchInfo();
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  handleize(value) {
    return String(value ?? '')
      .toLowerCase()
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

if (!customElements.get('boost-search')) {
  customElements.define('boost-search', BoostSearch);
}
